import React, { useRef, useState } from 'react';
import { Search, Plus, X, PanelLeft, Moon, Sun } from 'lucide-react';
import { useStore } from '../../store';
import { SyncIndicator } from '../ui';
import { useSearch } from '../../hooks';
import { useDarkMode } from '../../hooks/useDarkMode';

const C = { amber100:'#FFF4E0', amber300:'#F5A623', amber400:'#C4811A', ink:'#1A1814', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF' };
const font = { body:"'DM Sans',sans-serif" };

const topBarStyles = `
  .topbar-search { flex: 1; position: relative; max-width: 500px; }
  .topbar-breadcrumb { font-size:15px; font-weight:700; color:#1A1814; font-family:'DM Sans',sans-serif; flex-shrink:0; white-space:nowrap; max-width:160px; overflow:hidden; text-overflow:ellipsis; }
  .topbar-new-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:10px; background:linear-gradient(135deg,#F5A623,#C4811A); border:none; cursor:pointer; color:#fff; font-weight:700; font-size:13px; font-family:'DM Sans',sans-serif; flex-shrink:0; box-shadow:0 4px 12px #F5A62344; white-space:nowrap; }
  .topbar-divider { width:1px; height:18px; background:#E8E4DC; flex-shrink:0; }
  .topbar-dark-btn { background:none; border:1.5px solid #E8E4DC; cursor:pointer; color:#7A7570; padding:7px; display:flex; border-radius:9px; flex-shrink:0; }
  @media(max-width:600px) {
    .topbar-breadcrumb { display: none; }
    .topbar-divider    { display: none; }
    .topbar-dark-btn   { display: none; }
    .topbar-new-btn    { padding: 8px 12px; font-size: 12px; }
    .topbar-new-label  { display: none; }
  }
  @media(max-width:400px) {
    .topbar-new-btn { padding: 8px 10px; }
  }
`;

const TopBar: React.FC = () => {
  // Individual selectors — no inline objects
  const createNote      = useStore(s => s.createNote);
  const toggleSidebar   = useStore(s => s.toggleSidebar);
  const syncStatus      = useStore(s => s.syncStatus);
  const activeView      = useStore(s => s.activeView);
  const activeFolderId  = useStore(s => s.activeFolderId);
  const activeTag       = useStore(s => s.activeTag);
  const folders         = useStore(s => s.folders);
  const setOpenNote     = useStore(s => s.setOpenNote);

  const [localQuery, setLocalQuery] = useState('');
  const { handleSearch, clearSearch } = useSearch();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (val: string) => {
    setLocalQuery(val);
    handleSearch(val, activeFolderId, activeTag);
  };

  const handleClear = () => { setLocalQuery(''); clearSearch(); inputRef.current?.focus(); };

  const handleNewNote = async () => {
    const note = await createNote(activeFolderId);
    setOpenNote(note.id);
  };

  const breadcrumb = () => {
    if (activeView === 'all')    return 'All notes';
    if (activeView === 'pinned') return 'Pinned notes';
    if (activeView === 'search') return 'Search results';
    if (activeView === 'tag')    return `#${activeTag}`;
    if (activeView === 'folder') return folders.find(f => f.id === activeFolderId)?.name || 'Folder';
    return 'Jötr';
  };

  return (
    <>
    <style>{topBarStyles}</style>
    <div style={{ height:60, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:12, padding:'0 20px', background:C.white, flexShrink:0 }}>
      <button onClick={toggleSidebar} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4, display:'flex', borderRadius:7, flexShrink:0 }}>
        <PanelLeft size={18}/>
      </button>

      <span className="topbar-breadcrumb">
        {breadcrumb()}
      </span>

      <div className="topbar-divider"/>

      <div style={{ flex:1, position:'relative', maxWidth:500 }}>
        <Search size={15} color={C.muted} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
        <input
          ref={inputRef}
          value={localQuery}
          onChange={e => handleChange(e.target.value)}
          placeholder="Search all notes…"
          style={{ width:'100%', height:38, padding:'0 36px 0 38px', borderRadius:10, border:`1.5px solid ${localQuery ? C.amber300 : C.border}`, fontFamily:font.body, fontSize:14, color:C.ink, background:localQuery ? C.amber100 : C.surface, outline:'none', transition:'border-color .15s, background .15s', boxShadow:localQuery ? `0 0 0 3px ${C.amber300}22` : 'none' }}
        />
        {localQuery && (
          <button onClick={handleClear} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.muted, padding:2, display:'flex' }}>
            <X size={14}/>
          </button>
        )}
      </div>

      <SyncIndicator status={syncStatus}/>

      <button onClick={toggleDark} title={isDark ? 'Light mode' : 'Dark mode'} className="topbar-dark-btn">
        {isDark ? <Sun size={15}/> : <Moon size={15}/>}
      </button>

      <button onClick={handleNewNote} className="topbar-new-btn" title="New note (Ctrl/⌘+N)">
        <Plus size={15}/><span className="topbar-new-label">New note</span>
      </button>
    </div>
    </>
  );
};

export default TopBar;